/**
 * Load a ready-made animation file — a Mixamo `.fbx`, a Meshy/Blender `.glb` — and hand back the
 * skeleton it drives together with the clips on it.
 *
 * This is deliberately *not* `importGlbFromArrayBuffer`. That one imports a **model**: it demands
 * geometry, bakes world transforms and normalises a root for the viewport. An animation file has
 * no model to speak of — Mixamo's "without skin" download is a skeleton and nothing else — and
 * nothing here ever reaches the viewport. What matters is the bind pose and the clips.
 *
 * FBX support exists because Mixamo does not export glTF at all. `FBXLoader` is pulled in with a
 * dynamic import so its ~110 KB never enters the main bundle for users who only ever open GLBs.
 *
 * No network: the editor reads the bytes the user picked, exactly like every other import here.
 */

import { Bone, PropertyBinding } from 'three';
import type { AnimationClip, Object3D } from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';

import { AppError } from '../shared';
import { analyzeSkeleton, type SourceBoneInfo } from '../rig/sourceSkeleton';
import { hasTranslationTracks, inferFps } from '../rig/clipSampler';
import { MIN_RIGGED_BONE_COUNT } from './glbImport';
import { createLocalOnlyManager } from './localOnly';

export type AnimationSourceFormat = 'glb' | 'fbx';

/** What the import dialog shows for one clip in the file, before anything is baked. */
export interface AnimationSourceClip {
  /** Index into `ImportedAnimationSource.clips`. */
  index: number;
  /** The name the file gives it — Mixamo always says "mixamo.com". */
  name: string;
  durationSeconds: number;
  /** Authoring rate guessed from keyframe spacing, or `null` when it cannot be told. */
  fps: number | null;
  /** The clip translates a node, i.e. it carries root motion. */
  hasRootMotion: boolean;
}

export interface ImportedAnimationSource {
  format: AnimationSourceFormat;
  /** The loaded scene root. Bones live under it; it is never added to the viewport. */
  root: Object3D;
  /** Bind-pose analysis of the skeleton the clips drive. */
  bones: SourceBoneInfo[];
  clips: AnimationClip[];
  clipInfo: AnimationSourceClip[];
  /** True when no `SkinnedMesh` claims the bones, so "rest" is the loaded node transforms. */
  skinless: boolean;
  warnings: string[];
}

// ---------------------------------------------------------------------------
// Format sniffing
// ---------------------------------------------------------------------------

const GLB_MAGIC = 0x46546c67; // "glTF", little-endian
const FBX_BINARY_MAGIC = 'Kaydara FBX Binary';

/**
 * Decide the format from the bytes, falling back to the extension.
 *
 * The magic number is trusted over the name: a file saved as `walk.fbx` that is really a GLB is
 * far more common than the reverse, and picking the wrong loader produces a baffling parse error.
 */
export function sniffAnimationFormat(buffer: ArrayBuffer, fileName = ''): AnimationSourceFormat {
  if (buffer.byteLength >= 4) {
    const view = new DataView(buffer);
    if (view.getUint32(0, true) === GLB_MAGIC) return 'glb';
  }
  if (buffer.byteLength >= FBX_BINARY_MAGIC.length) {
    const head = new Uint8Array(buffer, 0, FBX_BINARY_MAGIC.length);
    let text = '';
    for (const byte of head) text += String.fromCharCode(byte);
    if (text === FBX_BINARY_MAGIC) return 'fbx';
  }
  return /\.fbx$/i.test(fileName) ? 'fbx' : 'glb';
}

// ---------------------------------------------------------------------------
// Loading
// ---------------------------------------------------------------------------

function describeError(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'object' && error !== null && 'message' in error) {
    return String((error as { message: unknown }).message);
  }
  return String(error);
}

function parseGlb(buffer: ArrayBuffer): Promise<{ root: Object3D; clips: AnimationClip[] }> {
  return new Promise((resolve, reject) => {
    // Local-only manager: an imported animation must never be able to make the app fetch.
    const loader = new GLTFLoader(createLocalOnlyManager('animation file'));
    const fail = (error: unknown) =>
      reject(
        new AppError(
          'ANIM_PARSE_FAILED',
          `The animation file could not be read: ${describeError(error)}`,
          error,
        ),
      );
    try {
      loader.parse(
        buffer,
        '',
        (gltf) => {
          const root = gltf.scene ?? gltf.scenes?.[0];
          if (!root) {
            fail(new Error('the file contains no scene'));
            return;
          }
          resolve({ root, clips: gltf.animations ?? [] });
        },
        fail,
      );
    } catch (error) {
      fail(error);
    }
  });
}

async function parseFbx(buffer: ArrayBuffer): Promise<{ root: Object3D; clips: AnimationClip[] }> {
  // Dynamic: keeps FBXLoader out of the bundle for anyone who never imports an FBX.
  const { FBXLoader } = await import('three/addons/loaders/FBXLoader.js');
  try {
    const group = new FBXLoader(createLocalOnlyManager('animation file')).parse(buffer, '');
    return { root: group, clips: group.animations ?? [] };
  } catch (error) {
    throw new AppError(
      'ANIM_PARSE_FAILED',
      `The FBX could not be read: ${describeError(error)}. Files written by FBX 6.x are not ` +
        'supported — re-export as FBX 7.x (binary).',
      error,
    );
  }
}

// ---------------------------------------------------------------------------
// Bone names
// ---------------------------------------------------------------------------

/**
 * Both loaders already run every node name through `PropertyBinding.sanitizeNodeName`, and they
 * apply it to animation track names too, so names and tracks agree. What neither guarantees is
 * *uniqueness*: `GLTFLoader` uniquifies, `FBXLoader` does not.
 *
 * Duplicates are rejected rather than renamed. Two bones called `Bone` produce two tracks both
 * called `Bone.quaternion`, and no rename can say which was meant — silently animating one arm
 * twice and freezing the other is worse than refusing the file.
 */
function assertUsableBoneNames(root: Object3D): void {
  const seen = new Set<string>();
  const duplicates = new Set<string>();
  const unsafe = new Set<string>();

  root.traverse((o) => {
    if ((o as Bone).isBone !== true) return;
    const name = o.name ?? '';
    if (name === '' || PropertyBinding.sanitizeNodeName(name) !== name) unsafe.add(name);
    if (seen.has(name)) duplicates.add(name);
    seen.add(name);
  });

  if (duplicates.size > 0) {
    const list = [...duplicates].slice(0, 5).join(', ');
    throw new AppError(
      'ANIM_DUPLICATE_BONES',
      `This file has more than one bone named ${list}. Animation tracks are matched to bones by ` +
        'name, so there is no way to tell which bone each track meant. Re-export it with unique ' +
        'bone names.',
      { duplicates: [...duplicates] },
    );
  }
  if (unsafe.size > 0) {
    const list = [...unsafe].slice(0, 5).map((n) => `"${n}"`).join(', ');
    throw new AppError(
      'ANIM_UNSAFE_BONE_NAMES',
      `Bone name(s) ${list} cannot appear in an animation track. Re-export without "[ ] . : /" ` +
        'in bone names.',
      { unsafe: [...unsafe] },
    );
  }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Parse an animation file that is already in memory.
 *
 * @throws AppError `ANIM_PARSE_FAILED`, `ANIM_NO_SKELETON`, `ANIM_NO_CLIPS`,
 *   `ANIM_DUPLICATE_BONES` or `ANIM_UNSAFE_BONE_NAMES`.
 */
export async function importAnimationBuffer(
  buffer: ArrayBuffer,
  fileName = '',
): Promise<ImportedAnimationSource> {
  if (!(buffer instanceof ArrayBuffer) || buffer.byteLength === 0) {
    throw new AppError('ANIM_PARSE_FAILED', 'The file is empty or is not a binary buffer.');
  }

  const format = sniffAnimationFormat(buffer, fileName);
  const { root, clips } = format === 'fbx' ? await parseFbx(buffer) : await parseGlb(buffer);

  const warnings: string[] = [];
  assertUsableBoneNames(root);

  root.updateMatrixWorld(true);
  const analysis = analyzeSkeleton(root);

  if (analysis.bones.length < MIN_RIGGED_BONE_COUNT) {
    throw new AppError(
      'ANIM_NO_SKELETON',
      `This file has ${analysis.bones.length} bone(s); an animation needs at least ` +
        `${MIN_RIGGED_BONE_COUNT} to describe a humanoid. Make sure the skeleton was included in ` +
        'the export.',
      { boneCount: analysis.bones.length },
    );
  }

  if (clips.length === 0) {
    throw new AppError(
      'ANIM_NO_CLIPS',
      'This file contains a skeleton but no animation. Export it with the animation included, ' +
        'or pick the file that has the motion in it.',
    );
  }

  const skinless = analysis.skinnedMeshes.length === 0;
  if (skinless) {
    warnings.push(
      'This file has no skinned mesh, so its rest pose is taken from the bone transforms as ' +
        'saved. If the exporter wrote an animation frame there instead of the bind pose, the ' +
        'imported motion will be offset — compare against a "with skin" export if it looks wrong.',
    );
  }

  const clipInfo: AnimationSourceClip[] = clips.map((clip, index) => {
    if (clip.duration < 0) clip.resetDuration();
    return {
      index,
      name: clip.name,
      durationSeconds: Math.max(0, clip.duration),
      fps: inferFps(clip),
      hasRootMotion: hasTranslationTracks(clip),
    };
  });

  return { format, root, bones: analysis.bones, clips, clipInfo, skinless, warnings };
}

/** Parse a picked `File` (or any `Blob`). The file name is used only to break a format tie. */
export async function importAnimationFile(file: File | Blob): Promise<ImportedAnimationSource> {
  let buffer: ArrayBuffer;
  try {
    buffer = await file.arrayBuffer();
  } catch (error) {
    throw new AppError(
      'ANIM_PARSE_FAILED',
      `The file could not be read: ${describeError(error)}`,
      error,
    );
  }
  return importAnimationBuffer(buffer, file instanceof File ? file.name : '');
}

/**
 * A default clip name for the file. Mixamo names every clip "mixamo.com", so the file name is
 * almost always the more useful label; the clip's own name wins when it says something.
 */
export function defaultClipName(fileName: string, clipName: string): string {
  const trimmed = clipName.trim();
  const generic = trimmed === '' || /^mixamo\.com$/i.test(trimmed) || /^take\s*0*1$/i.test(trimmed);
  if (!generic) return trimmed;
  const base = fileName.replace(/\.[^.]+$/, '').trim();
  return base === '' ? 'Imported animation' : base;
}
